import { CommonModule, formatNumber } from '@angular/common';
import {
    Component,
    InputSignal,
    Signal,
    WritableSignal,
    computed,
    inject,
    input,
    signal,
    effect,
    OnDestroy,
    OnInit,
    output,
} from '@angular/core';
import {
    IqEventBusBrokerService,
    IqEventBusBrokerHandler,
} from '@dat/iq-event-bus';
import { isNumber, get, isEqual, isEmpty } from 'lodash';
import { SearchInfoLt } from '../models';
import { z } from 'zod';

const TimeframeSchema = z.enum([
    'THIRTY_DAYS_BACK',
    'EIGHT_DAYS_BACK',
    'PREV_BUSINESS_DAY',
    'CURRENT',
    'EIGHT_DAY_FORECAST',
    '',
]);

export type Timeframe = z.infer<typeof TimeframeSchema>;

const LtGaugeQueryPayloadSchema = z.object({
    id: z.string(),
    loads: z.number().nullable(),
    trucks: z.number().nullable(),
    ratio: z.number().nullable(),
    timeframe: TimeframeSchema.nullable(),
    loaded: z.boolean(),
    hasError: z.boolean().default(false),
});

export type LtGaugeQueryPayload = z.infer<typeof LtGaugeQueryPayloadSchema>;

@Component({
    selector: 'iq-mc-gauge-query-lt',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-mc-gauge-query-lt.component.html',
    styleUrl: './iq-mc-gauge-query-lt.component.scss',
})
export class IqMcGaugeQueryLtComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    data: InputSignal<LtGaugeQueryPayload> = input({});
    onClick = output();

    clickable: InputSignal<boolean> = input(true);
    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    currentSearchLt: WritableSignal<SearchInfoLt> = signal({});
    dataInfoLt: Signal<string> = computed(
        (): string => {
            const loaded = this.loadedLt();
            const hasError = this.errorLt();

            if (!loaded || hasError) return '-- L/T Ratio';

            const ratioString: string = this.ratio()?.toFixed(1) ?? '--';

            return ` ${ratioString} L/T Ratio`;
        },
        {
            equal: isEqual,
        },
    );

    loadedLt: Signal<boolean> = computed(
        (): boolean => {
            return this._payload().loaded;
        },
        {
            equal: isEqual,
        },
    );
    errorLt: Signal<boolean> = computed(
        (): boolean => {
            return this._payload().hasError;
        },
        {
            equal: isEqual,
        },
    );
    loads: Signal<number> = computed(
        (): number => {
            return get(this._payload(), 'loads', 0);
        },
        {
            equal: isEqual,
        },
    );
    trucks: Signal<number> = computed(
        (): number => {
            return get(this._payload(), 'trucks', 0);
        },
        {
            equal: isEqual,
        },
    );
    ratio: Signal<number> = computed(
        (): number => {
            return get(this._payload(), 'ratio', 0);
        },
        {
            equal: isEqual,
        },
    );
    timeframe: Signal<Timeframe> = computed(
        (): Timeframe => {
            return get(this._payload(), 'timeframe', 'CURRENT' as Timeframe);
        },
        {
            equal: isEqual,
        },
    );

    trucksLabel: Signal<string> = computed(
        (): string => {
            const trucks = this.trucks();
            const loaded = this.loadedLt();
            const hasError = this.errorLt();

            const labelSuffix: string =
                isEqual(this.timeframe(), 'THIRTY_DAYS_BACK') ||
                isEqual(this.timeframe(), 'EIGHT_DAYS_BACK')
                    ? 'Average Trucks'
                    : 'Trucks';
            if (!loaded || hasError || !isNumber(trucks)) return '-- Trucks';
            return `${formatNumber(trucks, 'en-US', '1.1-1')} ${labelSuffix}`;
        },
        {
            equal: isEqual,
        },
    );
    loadsLabel: Signal<string> = computed(
        (): string => {
            const loads = this.loads();
            const loaded = this.loadedLt();
            const hasError = this.errorLt();

            const labelSuffix: string =
                isEqual(this.timeframe(), 'THIRTY_DAYS_BACK') ||
                isEqual(this.timeframe(), 'EIGHT_DAYS_BACK')
                    ? 'Average Loads'
                    : 'Loads';

            if (!loaded || hasError || !isNumber(loads)) return '-- Loads';
            return `${formatNumber(loads, 'en-US', '1.1-1')} ${labelSuffix}`;
        },
        {
            equal: isEqual,
        },
    );
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};
    private _payload: WritableSignal<LtGaugeQueryPayload> = signal({
        id: '',
        loads: 0,
        trucks: 0,
        ratio: 0,
        loaded: false,
    });

    parseUpdateLtQueryGuaguePayload(payload: LtGaugeQueryPayload) {
        const test: any = LtGaugeQueryPayloadSchema.safeParse(payload);
        if (!test.success) {
            console.warn('LtGaugeQueryPayloadSchema Invalid Payload');
            console.log(test?.error?.issues);
            return;
        }

        this._payload.set(payload);
    }

    constructor() {
        effect(() => {
            const newData = this.data();
            if (!isEmpty(newData)) {
                this.parseUpdateLtQueryGuaguePayload(newData);
            }
        }, { allowSignalWrites: true });

        this._deregisterMap = this._bus.registerMap({
            updateLtQueryGauge: {
                callback: (payload: LtGaugeQueryPayload) => {
                    if (payload.id !== this.id()) return;
                    const test: any =
                        LtGaugeQueryPayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'LtGaugeQueryPayloadSchema Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }

                    this._payload.set(payload);

                    if (this.loadedLt()) {
                        this._bus.dispatch({
                            name: 'type:tracking:log',
                            payload: {
                                type: 'SEGMENT',
                                eventType: 'TRACK',
                                eventName: 'RateView LT Gauge Displayed',
                                interaction: 'load',
                            },
                        });

                        this._bus.dispatch({
                            name: 'rateviewLoadedLtGauge',
                            payload: {
                                id: this.id(),
                            },
                        });
                    }
                },
            },
        });
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregisterMap);
    }

    async click(): Promise<void> {
        if (!this.loadedLt()) return;

        /**
         * This dispatches an event when the component is clicked
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onClick": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "id",
         *                "operator": "eq",
         *                "value": "COMPONENT_ID"
         *            },
         *            {
         *                "path": "componentName",
         *                "operator": "eq",
         *                "value": "IqMcGaugeQueryLt"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBus.dispatch({
                name: 'type:click',
                payload: {
                    id: this.id(),
                    componentName: 'IqMcGaugeQueryLt',
                    lt: {
                        ...this.currentSearchLt(),
                    },
                },
            });

        dispatch();
        this.onClick.emit();
    }
}
